import React from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import client, { url } from '../../lib/api/client';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(180deg, #0d0d0d 0%, #1a1a2e 100%);
  color: #fff;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem 2rem 3rem;
  box-sizing: border-box;
`;

const PlayerArea = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
`;

const VideoSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const TrackListPanel = styled.div`
  width: 320px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  overflow: hidden;
  max-height: 480px;
`;

const TrackListHeader = styled.div`
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TrackListCount = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 400;
`;

const TrackListScroll = styled.div`
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
`;

const TrackItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  gap: 0.75rem;
  cursor: pointer;
  transition: background 0.15s;
  background: ${props => props.active ? 'rgba(231, 76, 138, 0.12)' : 'transparent'};
  border-left: 3px solid ${props => props.active ? '#e74c8a' : 'transparent'};

  &:hover {
    background: ${props => props.active ? 'rgba(231, 76, 138, 0.15)' : 'rgba(255, 255, 255, 0.06)'};
  }
`;

const TrackNumber = styled.div`
  font-size: 0.8rem;
  color: ${props => props.active ? '#e74c8a' : 'rgba(255, 255, 255, 0.35)'};
  width: 20px;
  text-align: center;
  flex-shrink: 0;
`;

const TrackThumb = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const TrackItemTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.82rem;
  color: ${props => props.active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  font-weight: ${props => props.active ? '600' : '400'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VideoFrameWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: ${props => props.compact ? '20%' : '56.25%'};
  overflow: hidden;
  border-radius: 12px;
  background-color: #000;
  transition: padding-top 0.3s ease;
`;

const VideoFrameInner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const EmptyPlayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  border-radius: 12px;
  gap: 1rem;
`;

const EmptyIcon = styled.div`
  font-size: 2.5rem;
  opacity: 0.4;
`;

const EmptyText = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 400;
`;

const NowPlayingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  margin-bottom: 1rem;
  flex-shrink: 0;
`;

const NowPlayingThumb = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
`;

const NowPlayingInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const NowPlayingLabel = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #e74c8a;
  font-weight: 600;
  margin-bottom: 2px;
`;

const NowPlayingTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #fff;
`;

const PlaylistArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const PlaylistHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  flex-shrink: 0;
`;

const PlaylistTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
`;

const PlaylistCount = styled.span`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
`;


const PlaylistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.25rem;
`;

const PlaylistCard = styled.div`
  cursor: pointer;
  border-radius: 8px;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  ${props => props.active && css`
    background: rgba(231, 76, 138, 0.1);
    border-radius: 8px;
  `}
`;

const CardThumbWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
`;

const CardThumbImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CardNoThumb = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  opacity: 0.25;
`;

const CardPlayingOverlay = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 4px;
  padding: 4px 6px;
  display: flex;
  align-items: flex-end;
  gap: 2px;
`;

const CardInfo = styled.div`
  padding: 8px 4px 4px;
`;

const CardTitle = styled.div`
  font-size: 0.9rem;
  font-weight: ${props => props.active ? '600' : '500'};
  color: ${props => props.active ? '#fff' : 'rgba(255,255,255,0.9)'};
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardSub = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 4px;
  line-height: 1.3;
`;

const pulse = keyframes`
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
`;

const PlayingBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 14px;
  justify-content: center;
`;

const PlayingBar = styled.div`
  width: 3px;
  background: #e74c8a;
  border-radius: 1px;
  animation: ${pulse} ${props => props.duration || '0.8s'} ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  height: 100%;
  transform-origin: bottom;
`;

/* 이벤트 로그 (렌더 주석처리 하되 로직 유지) */
const EventLogSection = styled.div`
  margin: 1rem auto 0 auto;
  width: 640px;
  max-width: 640px;
  background-color: #fafafa;
  border-radius: 0.4rem;
  border: 1px solid #e0e0e0;
  padding: 0.75rem 0.9rem;
  display: flex;
  flex-direction: column;
  height: 150px;
`;

const EventLogTitle = styled.div`
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #555;
`;

const EventLogList = styled.div`
  flex: 1;
  overflow-y: auto;
  font-size: 0.8rem;
  color: #444;
`;

const EventLogItem = styled.div`
  margin-bottom: 0.25rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const NoticeBox = styled.div`
  border-radius: 10px;
  padding: 0.9rem 1.1rem;
  margin-bottom: 1rem;
  background: ${props => props.warn ? 'rgba(231, 76, 60, 0.14)' : 'rgba(46, 204, 113, 0.12)'};
  border: 1px solid ${props => props.warn ? 'rgba(231, 76, 60, 0.45)' : 'rgba(46, 204, 113, 0.35)'};
`;

const NoticeTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${props => props.warn ? '#ff8a80' : '#7ee2a8'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const NoticeText = styled.div`
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
  margin-top: 0.5rem;
`;

const NoticeList = styled.ol`
  margin: 0.4rem 0 0 0;
  padding-left: 1.1rem;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.7;
`;

const CommandBox = styled.div`
  margin-top: 0.5rem;
  padding: 0.6rem 0.7rem;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 6px;
  font-family: monospace;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.8);
  word-break: break-all;
  white-space: pre-wrap;
`;

const NoticeButton = styled.button`
  margin-top: 0.5rem;
  margin-right: 0.4rem;
  padding: 0.35rem 0.8rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }
`;

/* ── 소스 탭 · 내 재생목록 편집 UI ─────────────────────────────────────────── */

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.4rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 4px;
`;

const TabButton = styled.button`
  padding: 0.5rem 1.1rem;
  font-size: 0.88rem;
  font-weight: ${props => props.active ? '700' : '500'};
  color: ${props => props.active ? '#fff' : 'rgba(255,255,255,0.5)'};
  background: ${props => props.active ? 'rgba(231, 76, 138, 0.85)' : 'transparent'};
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    color: #fff;
    background: ${props => props.active ? 'rgba(231, 76, 138, 0.95)' : 'rgba(255,255,255,0.08)'};
  }
`;

const MyListBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const TextInput = styled.input`
  padding: 0.5rem 0.7rem;
  font-size: 0.85rem;
  color: #fff;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 6px;
  outline: none;
  min-width: ${props => props.wide ? '340px' : '200px'};
  flex: ${props => props.wide ? '1' : '0 0 auto'};

  &::placeholder { color: rgba(255, 255, 255, 0.3); }
  &:focus { border-color: rgba(231, 76, 138, 0.7); }
`;

const ActionButton = styled.button`
  padding: 0.5rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${props => props.danger ? '#ff8a80' : '#fff'};
  background: ${props => props.primary ? 'rgba(231, 76, 138, 0.85)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.18)'};
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${props => props.primary ? 'rgba(231, 76, 138, 1)' : 'rgba(255, 255, 255, 0.18)'};
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const MyErrorText = styled.div`
  font-size: 0.8rem;
  color: #ff8a80;
  margin-bottom: 0.75rem;
`;

const EmptyNote = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
  padding: 2rem 0;
  text-align: center;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const CardActionButton = styled.button`
  flex: 1;
  padding: 0.35rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.danger ? '#ff8a80' : 'rgba(255,255,255,0.8)'};
  background: ${props => props.primary ? 'rgba(231, 76, 138, 0.8)' : 'rgba(255, 255, 255, 0.09)'};
  border: 0;
  border-radius: 5px;
  cursor: pointer;

  &:hover { background: ${props => props.primary ? 'rgba(231, 76, 138, 1)' : 'rgba(255, 255, 255, 0.18)'}; }
`;

const EditorPanel = styled.div`
  margin-top: 1.25rem;
  padding: 1rem 1.1rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
`;

const EditorTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EditorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
`;

const EditorHint = styled.div`
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.6;
  margin-bottom: 0.85rem;
`;

const EditItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const EditItemIndex = styled.div`
  width: 22px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.35);
  text-align: center;
  flex-shrink: 0;
`;

const EditItemThumb = styled.img`
  width: 40px;
  height: 30px;
  border-radius: 3px;
  object-fit: cover;
  flex-shrink: 0;
`;

const EditItemTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconButton = styled.button`
  width: 26px;
  height: 26px;
  font-size: 0.75rem;
  color: ${props => props.danger ? '#ff8a80' : 'rgba(255,255,255,0.7)'};
  background: rgba(255, 255, 255, 0.08);
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.18); color: #fff; }
  &:disabled { opacity: 0.25; cursor: default; }
`;

const YOUTUBE_API_KEY = 'AIzaSyAU1Rzat88vvzQvUTh7HQ6NK9mRnpo6XrA';

const RunningTrackMusicPage = () => {
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const clubId = params.get('clubId');
  const clubName = params.get('clubName');

  const [currentPlaylistIndex, setCurrentPlaylistIndex] = React.useState(-1);
  const [eventLogs, setEventLogs] = React.useState([]);
  const [bgBlockCount, setBgBlockCount] = React.useState(0);   // 백그라운드에서 브라우저가 재생을 막은 횟수
  const [noticeDismissed, setNoticeDismissed] = React.useState(false);
  const [autoplayPolicy, setAutoplayPolicy] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const [playlist, setPlaylist] = React.useState([]);
  const [playlistLoaded, setPlaylistLoaded] = React.useState(false);
  const [playlistItems, setPlaylistItems] = React.useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = React.useState(-1);
  const playerRef = React.useRef(null);
  const lastStateRef = React.useRef(null);
  const delayTimeoutRef = React.useRef(null);
  const lastTickRef = React.useRef(0);
  const maxTickGapRef = React.useRef(0);
  const unexpectedPauseCountRef = React.useRef(0);
  const failedIdxRef = React.useRef(new Set());   // 임베드 금지 등으로 재생 불가였던 곡
  const startWatchRef = React.useRef(null);       // 곡 전환 후 실제 재생 시작 감시
  const lastBgResumeAtRef = React.useRef(0);
  const isDelayedPlayResumeRef = React.useRef(false);
  const ytApiReadyRef = React.useRef(false);
  const consecutiveErrorRef = React.useRef(0);
  const watchdogRef = React.useRef(null);
  const lastActivityRef = React.useRef(Date.now());
  const playCountRef = React.useRef(0);
  const playlistEndTimerRef = React.useRef(null);
  const trackListRef = React.useRef(null);
  const resumeIndexRef = React.useRef(-1);
  const lastPlaylistIndexRef = React.useRef(-1);
  const [playerVersion, setPlayerVersion] = React.useState(0);

  /* ── 재생 소스 ──────────────────────────────────────────────────────────────
     플레이어를 무엇으로 만들지는 이 하나로 정해진다.
       {kind:'shared', listId, title}   — 어드민이 등록한 유튜브 플레이리스트
       {kind:'custom', playlistID, title, tracks:[{videoId,title,thumbnailUrl}]}
                                        — 지점이 직접 만든 목록
     ──────────────────────────────────────────────────────────────────────── */
  const [playTarget, setPlayTarget] = React.useState(null);
  const [sourceTab, setSourceTab] = React.useState('shared');

  //--> 내 재생목록 (클럽 스코프)
  const [myLists, setMyLists] = React.useState([]);
  const [myListsLoaded, setMyListsLoaded] = React.useState(false);
  const [newListTitle, setNewListTitle] = React.useState('');
  const [editingListID, setEditingListID] = React.useState(null);
  const [editingTitle, setEditingTitle] = React.useState('');
  const [editingItems, setEditingItems] = React.useState([]);
  const [newVideoUrl, setNewVideoUrl] = React.useState('');
  const [myBusy, setMyBusy] = React.useState(false);
  const [myError, setMyError] = React.useState('');

  // 페이지 배경색 설정
  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prevHtmlBg = html.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    const prevRootBg = root ? root.style.backgroundColor : '';

    html.style.backgroundColor = '#0d0d0d';
    body.style.backgroundColor = '#0d0d0d';
    if (root) root.style.backgroundColor = '#0d0d0d';

    return () => {
      html.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
      if (root) root.style.backgroundColor = prevRootBg;
    };
  }, []);

  // API에서 플레이리스트 로드
  React.useEffect(() => {
    const loadPlaylist = async () => {
      try {
        const res = await client.get(`${url}/api/runningtrack/youtubemusic`);
        if (res.data && res.data.result === 1 && res.data.infos.length > 0) {
          const list = res.data.infos.map(item => ({
            title: item.title,
            listId: item.url,
            thumbnailUrl: item.thumbnailUrl || '',
          }));
          setPlaylist(list);
        }
      } catch (e) {
        console.error('플레이리스트 로드 실패:', e);
      }
      setPlaylistLoaded(true);
    };
    loadPlaylist();

    // YouTube IFrame API 미리 로드
    if (!window.YT) {
      window.onYouTubeIframeAPIReady = () => {
        ytApiReadyRef.current = true;
      };
      const existingScript = document.getElementById('youtube-iframe-api');
      if (!existingScript) {
        const tag = document.createElement('script');
        tag.id = 'youtube-iframe-api';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
      }
    } else {
      ytApiReadyRef.current = true;
    }
  }, []);

  const currentVideo = playTarget || null;

  const addEventLog = React.useCallback((message) => {
    const now = new Date();
    const time = now.toTimeString().split(' ')[0];
    setEventLogs((prev) => {
      const next = [{ time, message }, ...prev];
      return next.length > 100 ? next.slice(0, 100) : next;
    });
  }, []);

  // 백그라운드에서 브라우저가 재생을 막은 것을 기록한다. (안내 배너 표시용)
  const markBackgroundBlocked = React.useCallback(() => {
    setBgBlockCount(function(c) { return c + 1; });
  }, []);

  // 실행 옵션 확인: --autoplay-policy=no-user-gesture-required 로 띄운 크롬은 'allowed'를 돌려준다.
  // (백그라운드 정지 해제 플래그와 함께 주는 값이라, 권장 방식으로 열렸는지 판별하는 지표로 쓴다)
  React.useEffect(() => {
    try {
      if (navigator.getAutoplayPolicy) {
        setAutoplayPolicy(navigator.getAutoplayPolicy('mediaelement'));
      }
    } catch (e) {}
  }, []);

  const getPlayerStateSafe = () => {
    try {
      return playerRef.current && playerRef.current.getPlayerState
        ? playerRef.current.getPlayerState()
        : -99;
    } catch (e) {
      return -99;
    }
  };

  const getPlaylistIndexSafe = () => {
    try {
      return playerRef.current && playerRef.current.getPlaylistIndex
        ? playerRef.current.getPlaylistIndex()
        : -1;
    } catch (e) {
      return -1;
    }
  };

  /* ── 재생 불가 곡 처리 ───────────────────────────────────────────────────────
     유튜브 오류 150/101 은 업로더가 임베드를 막은 영상이다. 이런 곡은 아무리 다시
     시도해도 재생되지 않으므로 기록해두고 건너뛴다. 게다가 에러 이벤트 없이
     버퍼링 상태로 멈춰 있는 경우도 있어(로그상 45초 무음), 곡 전환 후 재생이
     시작되는지 짧게 감시해서 빠르게 다음 곡으로 넘긴다.
     ──────────────────────────────────────────────────────────────────────────── */
  const playlistTotal = () => {
    try {
      const ids = playerRef.current && playerRef.current.getPlaylist ? playerRef.current.getPlaylist() : null;
      return ids ? ids.length : 0;
    } catch (e) {
      return 0;
    }
  };

  // fromIdx 다음의 '아직 실패하지 않은' 곡 번호. 전부 실패했으면 기록을 비우고 다시 돈다.
  const nextPlayableIndex = (fromIdx) => {
    const total = playlistTotal();
    if (total === 0) return 0;
    for (let step = 1; step <= total; step++) {
      const cand = (fromIdx + step) % total;
      if (!failedIdxRef.current.has(cand)) return cand;
    }
    failedIdxRef.current.clear();
    addEventLog('재생 가능한 곡이 없어 실패 기록을 초기화합니다.');
    return (fromIdx + 1) % total;
  };

  const cancelStartWatch = () => {
    if (startWatchRef.current) {
      clearTimeout(startWatchRef.current);
      startWatchRef.current = null;
    }
  };

  const watchTrackStart = (idx) => {
    cancelStartWatch();
    startWatchRef.current = setTimeout(() => {
      startWatchRef.current = null;
      const st = getPlayerStateSafe();
      if (st === 1 || st === 2) return;   // 재생 중이거나 싱크용 일시정지면 정상
      if (document.hidden) return;        // 백그라운드 정지는 별도 로직이 처리한다
      addEventLog((idx + 1) + '번 곡이 시작되지 않아 건너뜁니다. (state: ' + st + ')');
      failedIdxRef.current.add(idx);
      skipToNextTrack(idx);
    }, 7000);
  };

  const skipToNextTrack = (fromIdx) => {
    const player = playerRef.current;
    if (!player || !player.playVideoAt) return;
    const next = nextPlayableIndex(fromIdx);
    addEventLog('다음 곡(' + (next + 1) + '번)을 시도합니다.');
    try {
      player.playVideoAt(next);
      setCurrentTrackIndex(next);
      watchTrackStart(next);
    } catch (e) {
      addEventLog('다음 곡 재생 실패 - 플레이어를 재생성합니다.');
      consecutiveErrorRef.current = 0;
      resumeIndexRef.current = next;
      setPlayerVersion(function(v) { return v + 1; });
    }
  };

  const getYouTubeErrorMessage = (code) => {
    switch (code) {
      case 2:
        return '잘못된 요청 (영상 ID 형식 오류 등)';
      case 5:
        return 'HTML5 플레이어에서 재생할 수 없습니다.';
      case 100:
        return '영상이 삭제되었거나 비공개입니다.';
      case 101:
      case 150:
        return '업로더가 외부 사이트(임베드) 재생을 허용하지 않았습니다.';
      default:
        return '알 수 없는 오류입니다.';
    }
  };

  // 선택된 재생 소스가 변경될 때만 플레이어 생성
  React.useEffect(() => {
    if (!playTarget) return;

    const isCustom = playTarget.kind === 'custom';
    const customIds = isCustom ? playTarget.tracks.map(function(t) { return t.videoId; }) : [];
    if (isCustom && customIds.length === 0) return;

    const createPlayer = () => {
      if (!window.YT || !window.YT.Player) return;

      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }

      /* 내 재생목록은 유튜브에 존재하지 않는 임의의 곡 묶음이라 list 파라미터로는 만들 수 없다.
         첫 곡으로 임베드만 유효하게 띄우고(autoplay 0 — 여기서 울리면 곧이어 부르는
         loadPlaylist 와 소리가 겹친다), onReady 에서 loadPlaylist 로 진짜 목록을 싣는다.
         이렇게 해야 getPlaylist / getPlaylistIndex / playVideoAt 이 공용 목록과 똑같이
         동작해서, 아래의 3초 TV 동기화·실패곡 스킵·워치독·마지막곡 루프를 그대로 쓴다. */
      const playerConfig = isCustom
        ? {
            videoId: customIds[0],
            playerVars: { autoplay: 0, rel: 0 },
          }
        : {
            playerVars: {
              listType: 'playlist',
              list: playTarget.listId,
              autoplay: 1,
              rel: 0,
            },
          };

      playerRef.current = new window.YT.Player('youtube-player', {
        ...playerConfig,
        events: {
          onReady: () => {
            addEventLog('플레이어 준비 완료');
            lastActivityRef.current = Date.now();
            consecutiveErrorRef.current = 0;
            lastPlaylistIndexRef.current = -1;

            if (isCustom) {
              // 제목·썸네일은 우리 DB 값이 정본이라 YouTube Data API 를 다시 부르지 않는다.
              setPlaylistItems(playTarget.tracks.map(function(t) {
                return { videoId: t.videoId, title: t.title || t.videoId, thumbnailUrl: t.thumbnailUrl || '' };
              }));

              var startIdx = resumeIndexRef.current > 0 ? resumeIndexRef.current : 0;
              if (startIdx >= customIds.length) startIdx = 0;
              resumeIndexRef.current = -1;
              setCurrentTrackIndex(startIdx);

              try {
                // loadPlaylist 가 재생까지 시작한다.
                playerRef.current.loadPlaylist({ playlist: customIds, index: startIdx });
                watchTrackStart(startIdx);
              } catch (e) {
                addEventLog('재생목록을 싣지 못했습니다.');
              }
              return;
            }

            // 재생성 시 이전 위치 복원
            if (resumeIndexRef.current > 0 && playerRef.current && playerRef.current.playVideoAt) {
              var ri = resumeIndexRef.current;
              resumeIndexRef.current = -1;
              addEventLog((ri + 1) + '번 곡부터 이어서 재생합니다.');
              playerRef.current.playVideoAt(ri);
              setCurrentTrackIndex(ri);
              watchTrackStart(ri);
            }

            // 플레이어에서 곡 목록 가져오기
            try {
              var ids = playerRef.current && playerRef.current.getPlaylist ? playerRef.current.getPlaylist() : null;
              if (ids && ids.length > 0) {
                // 먼저 Track N으로 즉시 표시
                var initial = ids.map(function(id, i) {
                  return { title: 'Track ' + (i + 1), videoId: id, thumbnailUrl: '' };
                });
                setPlaylistItems(initial);
                setCurrentTrackIndex(0);

                // YouTube Data API로 실제 제목/썸네일 조회 (50개씩 배치)
                var batchSize = 50;
                var promises = [];
                for (var i = 0; i < ids.length; i += batchSize) {
                  var batch = ids.slice(i, i + batchSize);
                  promises.push(
                    fetch('https://www.googleapis.com/youtube/v3/videos?part=snippet&id=' + batch.join(',') + '&key=' + YOUTUBE_API_KEY)
                      .then(function(r) { return r.json(); })
                  );
                }
                Promise.all(promises).then(function(results) {
                  var titleMap = {};
                  results.forEach(function(data) {
                    if (data && data.items) {
                      data.items.forEach(function(item) {
                        var thumbs = item.snippet && item.snippet.thumbnails;
                        titleMap[item.id] = {
                          title: item.snippet.title,
                          thumbnailUrl: (thumbs && thumbs.default && thumbs.default.url) || '',
                        };
                      });
                    }
                  });
                  setPlaylistItems(function(prev) {
                    return prev.map(function(track) {
                      var info = titleMap[track.videoId];
                      return info ? { videoId: track.videoId, title: info.title, thumbnailUrl: info.thumbnailUrl } : track;
                    });
                  });
                }).catch(function() {});
              }
            } catch (e) {}
          },
          onStateChange: (event) => {
            const YTState = window.YT.PlayerState;
            const state = event.data;
            lastActivityRef.current = Date.now();

            if (state === YTState.PLAYING) {
              consecutiveErrorRef.current = 0;
              cancelStartWatch();
              // 재생 종료 타이머 취소 (새 곡 시작됨)
              if (playlistEndTimerRef.current) {
                clearTimeout(playlistEndTimerRef.current);
                playlistEndTimerRef.current = null;
              }
              // 현재 트랙 인덱스 추적 + 제목 업데이트
              try {
                if (playerRef.current && playerRef.current.getPlaylistIndex) {
                  var idx = playerRef.current.getPlaylistIndex();
                  if (idx >= 0) {
                    setCurrentTrackIndex(idx);
                    // Track N 제목이면 실제 제목으로 교체
                    var vData = playerRef.current.getVideoData ? playerRef.current.getVideoData() : null;
                    if (vData && vData.title) {
                      setPlaylistItems(function(prev) {
                        if (prev[idx] && prev[idx].title !== vData.title) {
                          var next = prev.slice();
                          next[idx] = { videoId: prev[idx].videoId, title: vData.title, thumbnailUrl: prev[idx].thumbnailUrl };
                          return next;
                        }
                        return prev;
                      });
                    }
                  }
                }
              } catch (e) {}
              if (isDelayedPlayResumeRef.current) {
                isDelayedPlayResumeRef.current = false;
                lastStateRef.current = state;
              } else {
                // 새 곡 시작 판정: 플레이리스트 인덱스가 실제로 바뀌었을 때만
                // (윈도우 복귀 시 BUFFERING/UNSTARTED → PLAYING 전이는 같은 곡이므로 제외)
                let curIdx = -1;
                try {
                  if (playerRef.current && playerRef.current.getPlaylistIndex) {
                    curIdx = playerRef.current.getPlaylistIndex();
                  }
                } catch (e) {}
                const isNewTrack = curIdx >= 0 && curIdx !== lastPlaylistIndexRef.current;
                if (isNewTrack && playerRef.current) {
                  lastPlaylistIndexRef.current = curIdx;
                  playCountRef.current += 1;

                  // 10곡마다 플레이어 재생성 (iframe 메모리 누적 방지)
                  if (playCountRef.current >= 10) {
                    addEventLog('메모리 관리를 위해 플레이어를 재생성합니다.');
                    playCountRef.current = 0;
                    try { resumeIndexRef.current = playerRef.current.getPlaylistIndex(); } catch(e) {}
                    setPlayerVersion(v => v + 1);
                    lastStateRef.current = state;
                    return;
                  }

                  playerRef.current.pauseVideo();
                  addEventLog('3초 후 재생 예정');

                  if (playerRef.current && playerRef.current.playVideo) {
                    try {
                      const data = playerRef.current.getVideoData?.();
                      const videoId = data?.video_id;
                      if (clubId && videoId) {
                        client.post(`${url}/api/runningtrack/musicplay/${clubId}`, { videoId }).catch(() => {});
                      }
                    } catch (e) {}
                  }

                  if (delayTimeoutRef.current) clearTimeout(delayTimeoutRef.current);
                  delayTimeoutRef.current = setTimeout(() => {
                    if (playerRef.current && playerRef.current.playVideo) {
                      isDelayedPlayResumeRef.current = true;
                      playerRef.current.playVideo();
                    }
                    delayTimeoutRef.current = null;
                  }, 3000);
                  lastStateRef.current = state;
                  return;
                }
              }

              try {
                const data = playerRef.current && playerRef.current.getVideoData
                  ? playerRef.current.getVideoData()
                  : null;
                const videoUrl = playerRef.current && playerRef.current.getVideoUrl
                  ? playerRef.current.getVideoUrl()
                  : null;
                if (data || videoUrl) {
                  addEventLog(
                    `현재 재생 중: ${data?.title || '제목 없음'} (ID: ${data?.video_id || '알 수 없음'})` +
                    (videoUrl ? ` | URL: ${videoUrl}` : '')
                  );
                }
              } catch (e) {}

              if (lastStateRef.current === YTState.PAUSED) {
                addEventLog('재생 재시작');
              } else if (lastStateRef.current === YTState.ENDED) {
                addEventLog('다음 곡 재생 시작');
              } else {
                addEventLog('재생 시작');
              }
            } else if (state === YTState.PAUSED) {
              addEventLog('일시정지');
              // 우리가 건 싱크용 3초 대기가 아닌데 멈췄다면, 창을 숨길 때 브라우저가
              // 백그라운드 미디어를 정지시킨 경우다. 곧바로 재생을 재개한다.
              if (!delayTimeoutRef.current && document.hidden && playerRef.current && playerRef.current.playVideo) {
                markBackgroundBlocked();
                // 재개 후 한동안 잘 재생됐다면 카운터를 초기화한다. 즉 정상 재개는 몇 번이든 반복한다.
                // 반대로 재개하자마자 다시 멈추는 상황(15초 이내)이 이어지면 소리가 끊겨 들리므로 물러선다.
                const sinceResume = Date.now() - lastBgResumeAtRef.current;
                if (sinceResume < 15000) unexpectedPauseCountRef.current += 1;
                else unexpectedPauseCountRef.current = 1;

                if (unexpectedPauseCountRef.current <= 5) {
                  addEventLog('브라우저가 정지시킴 - 재생을 재개합니다. (' + unexpectedPauseCountRef.current + '/5, 직전 재개 ' +
                    (lastBgResumeAtRef.current ? Math.round(sinceResume / 1000) + '초 전' : '없음') + ')');
                  lastBgResumeAtRef.current = Date.now();
                  try { playerRef.current.playVideo(); } catch (e) {}
                } else {
                  addEventLog('짧은 간격으로 반복 정지 - 창이 다시 표시될 때 복구합니다.');
                }
              }
            } else if (state === YTState.BUFFERING) {
              addEventLog('버퍼링 중');
            } else if (state === YTState.ENDED) {
              addEventLog('곡 재생 완료');

              // 마지막 곡이면 5초 타이머를 기다리지 않고 즉시 처음으로 되돌린다.
              // (재생이 멈춘 백그라운드 창에서는 타이머가 창을 열 때까지 실행되지 않을 수 있다)
              var endIdx = -1;
              var endTotal = 0;
              try {
                if (playerRef.current && playerRef.current.getPlaylistIndex) {
                  endIdx = playerRef.current.getPlaylistIndex();
                }
                var endIds = playerRef.current && playerRef.current.getPlaylist
                  ? playerRef.current.getPlaylist()
                  : null;
                endTotal = endIds ? endIds.length : 0;
              } catch (e) {}
              if (endTotal > 0 && endIdx === endTotal - 1 && playerRef.current && playerRef.current.playVideoAt) {
                if (playlistEndTimerRef.current) {
                  clearTimeout(playlistEndTimerRef.current);
                  playlistEndTimerRef.current = null;
                }
                addEventLog('마지막 곡 종료 - 처음부터 다시 재생합니다.');
                playerRef.current.playVideoAt(0);
                setCurrentTrackIndex(0);
                lastStateRef.current = state;
                return;
              }

              // 5초 내 다음 곡이 시작되지 않으면 플레이리스트 끝으로 간주 → 처음부터 재생
              if (playlistEndTimerRef.current) clearTimeout(playlistEndTimerRef.current);
              playlistEndTimerRef.current = setTimeout(function() {
                if (playerRef.current && playerRef.current.playVideoAt) {
                  addEventLog('마지막 곡 종료 - 처음부터 다시 재생합니다.');
                  playerRef.current.playVideoAt(0);
                  setCurrentTrackIndex(0);
                }
                playlistEndTimerRef.current = null;
              }, 5000);
            }

            lastStateRef.current = state;
          },
          onError: (event) => {
            const code = event && typeof event.data !== 'undefined' ? event.data : 'unknown';
            const desc = getYouTubeErrorMessage(code);
            addEventLog(`재생 실패 (code: ${code}) - ${desc}`);

            cancelStartWatch();
            consecutiveErrorRef.current += 1;

            // 이 곡은 다시 시도해도 같은 결과다. 기록해두고 다음부터 건너뛴다.
            const failedIdx = getPlaylistIndexSafe();
            if (failedIdx >= 0) failedIdxRef.current.add(failedIdx);

            if (consecutiveErrorRef.current >= 3) {
              addEventLog('연속 오류 발생으로 플레이어를 재생성합니다.');
              consecutiveErrorRef.current = 0;
              // 실패한 곡이 아니라 그 다음 재생 가능한 곡부터 복원한다.
              // (실패한 번호로 되돌리면 재생성 직후 같은 오류가 반복된다)
              resumeIndexRef.current = failedIdx >= 0 ? nextPlayableIndex(failedIdx) : 0;
              setPlayerVersion(v => v + 1);
              return;
            }

            if (playerRef.current && playerRef.current.playVideoAt) {
              skipToNextTrack(failedIdx >= 0 ? failedIdx : 0);
            } else {
              addEventLog('플레이어를 재생성합니다.');
              consecutiveErrorRef.current = 0;
              resumeIndexRef.current = failedIdx >= 0 ? nextPlayableIndex(failedIdx) : 0;
              setPlayerVersion(v => v + 1);
            }
          },
        },
      });

      // 워치독: 30초 이상 응답 없는 플레이어 감지 및 복구
      if (watchdogRef.current) clearInterval(watchdogRef.current);
      watchdogRef.current = setInterval(() => {
        if (!playerRef.current) return;
        var elapsed = Date.now() - lastActivityRef.current;
        if (elapsed < 30000) return;

        try {
          var state = playerRef.current.getPlayerState ? playerRef.current.getPlayerState() : -1;
          if (state === 1) {
            // PLAYING 상태이면 정상 - 타임스탬프만 갱신
            lastActivityRef.current = Date.now();
            return;
          }
          // 창이 숨겨진 동안에는 브라우저가 새 영상의 재생 시작을 보류시키므로
          // 다음 곡 이동이나 플레이어 재생성을 해도 재생되지 않고 곡만 건너뛰게 된다.
          // 창이 다시 표시될 때까지 재개 시도만 하고 대기한다.
          if (document.hidden) {
            lastActivityRef.current = Date.now();
            markBackgroundBlocked();
            addEventLog('창이 숨겨진 상태 - 곡 이동 없이 재생 재개만 시도합니다. (state: ' + state + ')');
            // 2=일시정지, -1=미시작, 5=대기 — 이미 로드된 영상이라 재개 명령이 먹힌다.
            // 3=버퍼링은 로딩 중이므로 건드리지 않는다.
            if ((state === 2 || state === -1 || state === 5) && playerRef.current.playVideo) {
              lastBgResumeAtRef.current = Date.now();
              try { playerRef.current.playVideo(); } catch (e2) {}
            }
            return;
          }

          addEventLog('플레이어 응답 없음 감지, 복구를 시도합니다.');
          lastActivityRef.current = Date.now();
          consecutiveErrorRef.current += 1;

          if (consecutiveErrorRef.current >= 3) {
            addEventLog('플레이어를 재생성합니다.');
            consecutiveErrorRef.current = 0;
            try { resumeIndexRef.current = playerRef.current.getPlaylistIndex(); } catch(e2) {}
            setPlayerVersion(v => v + 1);
          } else if (playerRef.current && playerRef.current.nextVideo) {
            playerRef.current.nextVideo();
          }
        } catch (e) {
          addEventLog('플레이어 오류 감지, 재생성합니다.');
          lastActivityRef.current = Date.now();
          consecutiveErrorRef.current = 0;
          try { resumeIndexRef.current = playerRef.current.getPlaylistIndex(); } catch(e2) {}
          setPlayerVersion(v => v + 1);
        }
      }, 15000);
    };

    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      const prevCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prevCallback) prevCallback();
        ytApiReadyRef.current = true;
        createPlayer();
      };
    }

    return () => {
      if (delayTimeoutRef.current) {
        clearTimeout(delayTimeoutRef.current);
        delayTimeoutRef.current = null;
      }
      cancelStartWatch();
      if (playlistEndTimerRef.current) {
        clearTimeout(playlistEndTimerRef.current);
        playlistEndTimerRef.current = null;
      }
      if (watchdogRef.current) {
        clearInterval(watchdogRef.current);
        watchdogRef.current = null;
      }
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [addEventLog, markBackgroundBlocked, playTarget, playerVersion]);

  // 타이머가 실제로 돌고 있는지 측정한다. (백그라운드 스로틀링/정지 진단용)
  React.useEffect(() => {
    lastTickRef.current = Date.now();
    maxTickGapRef.current = 0;
    const id = setInterval(() => {
      const now = Date.now();
      const gap = now - lastTickRef.current;
      lastTickRef.current = now;
      if (gap > maxTickGapRef.current) maxTickGapRef.current = gap;
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // 창 표시 상태 기록 + 백그라운드에서 멈춰 있던 재생 복구
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        maxTickGapRef.current = 0;
        lastTickRef.current = Date.now();
        // 최소화된 창은 화면 좌표가 화면 밖(윈도우 기준 -32000 부근)으로 잡힌다.
        // 최소화인지, 다른 창에 가려진 것인지에 따라 필요한 조치가 다르므로 함께 기록한다.
        const minimized = window.screenY < -10000 || window.screenX < -10000;
        addEventLog('창 숨김 [' + (minimized ? '최소화' : '가려짐/전환') + '] 플레이어 state: ' + getPlayerStateSafe() +
          ', 창 위치: ' + window.screenX + ',' + window.screenY);
      } else {
        const st = getPlayerStateSafe();
        addEventLog('창 복귀 - 백그라운드 최대 타이머 지연 ' + maxTickGapRef.current + 'ms (플레이어 state: ' + st + ')');
        unexpectedPauseCountRef.current = 0;
        lastBgResumeAtRef.current = 0;
        // 백그라운드에서 멈춰 있던 재생을 창이 돌아오는 즉시 되살린다.
        // (워치독의 30초를 기다리지 않도록)
        if (!delayTimeoutRef.current && (st === 2 || st === -1 || st === 5) && playerRef.current && playerRef.current.playVideo) {
          addEventLog('창 복귀 - 멈춰 있던 재생을 재개합니다.');
          try { playerRef.current.playVideo(); } catch (e) {}
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [addEventLog]);

  // 권장 실행 명령 (백그라운드 재생 중단 정책을 해제한 전용 크롬 창)
  const launchCommand =
    '"C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe"' +
    ' --disable-background-media-suspend' +
    ' --disable-backgrounding-occluded-windows' +
    ' --disable-renderer-backgrounding' +
    ' --autoplay-policy=no-user-gesture-required' +
    ' --user-data-dir="%LOCALAPPDATA%\\JetaMusicPlayer"' +
    ' --app="' + (typeof window !== 'undefined' ? window.location.href : '') + '"';

  const handleCopyCommand = () => {
    const done = function() {
      setCopied(true);
      setTimeout(function() { setCopied(false); }, 2000);
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(launchCommand).then(done).catch(function() {});
        return;
      }
    } catch (e) {}
    try {
      const ta = document.createElement('textarea');
      ta.value = launchCommand;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      done();
    } catch (e) {}
  };

  //--> 재생 소스를 바꾸기 전에 항상 거쳐야 하는 초기화. 이전 목록의 실패 기록·트랙 번호가
  //    새 목록으로 새면 엉뚱한 곡을 건너뛴다.
  const resetPlaybackState = () => {
    setPlaylistItems([]);
    setCurrentTrackIndex(-1);
    playCountRef.current = 0;
    lastPlaylistIndexRef.current = -1;
    resumeIndexRef.current = -1;
    failedIdxRef.current = new Set();
  };

  const handleSelectPlaylist = (index) => {
    const selected = playlist[index];
    resetPlaybackState();
    setCurrentPlaylistIndex(index);
    setPlayTarget({
      kind: 'shared',
      listId: selected?.listId,
      title: selected?.title || `index ${index}`,
    });
    addEventLog(`플레이리스트 선택: ${selected?.title || `index ${index}`}`);
  };

  /* ── 내 재생목록 ────────────────────────────────────────────────────────────
     지점이 직접 만드는 목록. clubId 쿼리파라미터가 스코프이며, 없으면 쓸 수 없다.
     ──────────────────────────────────────────────────────────────────────── */

  //--> 유튜브 영상 ID 추출. TV 앱(jetarracktv/src/common/YoutubeItem.js)에서 검증된 패턴이다.
  const extractVideoId = (input) => {
    const s = String(input || '').trim();
    if (!s) return null;
    let m = s.match(/[?&]v=([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    m = s.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    m = s.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    m = s.match(/\/shorts\/([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
    return null;
  };

  //--> 제목·썸네일 조회. 실패해도 곡은 담는다 — 쿼터 때문에 목록을 못 만들게 하지 않는다.
  const fetchVideoInfo = async (videoId) => {
    try {
      const res = await fetch(
        'https://www.googleapis.com/youtube/v3/videos?part=snippet&id=' + videoId + '&key=' + YOUTUBE_API_KEY);
      const data = await res.json();
      const item = data && data.items && data.items[0];
      if (item && item.snippet) {
        const thumbs = item.snippet.thumbnails;
        return {
          title: item.snippet.title || videoId,
          thumbnailUrl: (thumbs && ((thumbs.medium && thumbs.medium.url) || (thumbs.default && thumbs.default.url))) || '',
        };
      }
    } catch (e) {}
    return { title: videoId, thumbnailUrl: '' };
  };

  const loadMyLists = React.useCallback(async () => {
    if (!clubId) {
      setMyListsLoaded(true);
      return;
    }
    try {
      const res = await client.get(`${url}/api/runningtrack/musicplaylist/${clubId}`);
      if (res.data && res.data.result === 1) {
        setMyLists(res.data.playlists || []);
      }
    } catch (e) {
      console.error('내 재생목록 로드 실패:', e);
    }
    setMyListsLoaded(true);
  }, [clubId]);

  React.useEffect(() => {
    loadMyLists();
  }, [loadMyLists]);

  //--> 곡 조회만 한다. 편집 패널 상태는 건드리지 않는다 (재생 버튼도 이걸 쓴다).
  const fetchItems = async (playlistID) => {
    const res = await client.get(`${url}/api/runningtrack/musicplaylist/${playlistID}/item`);
    if (res.data && res.data.result === 1) return res.data.items || [];
    return [];
  };

  const loadEditingItems = async (playlistID) => {
    try {
      const items = await fetchItems(playlistID);
      setEditingItems(items);
      return items;
    } catch (e) {
      setMyError('곡 목록을 불러오지 못했습니다.');
    }
    setEditingItems([]);
    return [];
  };

  const handleCreateList = async () => {
    const title = newListTitle.trim();
    if (!title) {
      setMyError('목록 이름을 입력해주세요.');
      return;
    }
    setMyBusy(true);
    setMyError('');
    try {
      const res = await client.post(`${url}/api/runningtrack/musicplaylist/${clubId}`, { title });
      if (res.data && res.data.result === 1) {
        setNewListTitle('');
        await loadMyLists();
        addEventLog(`재생목록 생성: ${title}`);
      } else {
        setMyError((res.data && res.data.message) || '목록을 만들지 못했습니다.');
      }
    } catch (e) {
      setMyError('목록을 만들지 못했습니다.');
    }
    setMyBusy(false);
  };

  const handleOpenEditor = async (list) => {
    setMyError('');
    if (editingListID === list.clubMusicPlaylistID) {
      setEditingListID(null);
      return;
    }
    setEditingListID(list.clubMusicPlaylistID);
    setEditingTitle(list.title || '');
    setNewVideoUrl('');
    setEditingItems([]);
    await loadEditingItems(list.clubMusicPlaylistID);
  };

  const handleRenameList = async () => {
    const title = editingTitle.trim();
    if (!title || !editingListID) return;
    setMyBusy(true);
    setMyError('');
    try {
      const res = await client.patch(`${url}/api/runningtrack/musicplaylist/${editingListID}`, { title });
      if (res.data && res.data.result === 1) {
        await loadMyLists();
      } else {
        setMyError((res.data && res.data.message) || '이름을 바꾸지 못했습니다.');
      }
    } catch (e) {
      setMyError('이름을 바꾸지 못했습니다.');
    }
    setMyBusy(false);
  };

  const handleDeleteList = async (list) => {
    if (!window.confirm(`'${list.title}' 목록을 삭제할까요? 담긴 곡도 함께 지워집니다.`)) return;
    setMyBusy(true);
    setMyError('');
    try {
      await client.delete(`${url}/api/runningtrack/musicplaylist/${list.clubMusicPlaylistID}`);
      if (editingListID === list.clubMusicPlaylistID) setEditingListID(null);
      await loadMyLists();
      addEventLog(`재생목록 삭제: ${list.title}`);
    } catch (e) {
      setMyError('목록을 삭제하지 못했습니다.');
    }
    setMyBusy(false);
  };

  const handleAddTrack = async () => {
    const videoId = extractVideoId(newVideoUrl);
    if (!videoId) {
      setMyError('유튜브 영상 주소를 확인해주세요. (예: https://www.youtube.com/watch?v=...)');
      return;
    }
    setMyBusy(true);
    setMyError('');
    try {
      const info = await fetchVideoInfo(videoId);
      const res = await client.post(`${url}/api/runningtrack/musicplaylist/${editingListID}/item`, {
        videoId,
        title: info.title,
        thumbnailUrl: info.thumbnailUrl,
      });
      if (res.data && res.data.result === 1) {
        setNewVideoUrl('');
        await loadEditingItems(editingListID);
        await loadMyLists();
      } else {
        setMyError((res.data && res.data.message) || '곡을 추가하지 못했습니다.');
      }
    } catch (e) {
      setMyError('곡을 추가하지 못했습니다.');
    }
    setMyBusy(false);
  };

  const handleDeleteTrack = async (item) => {
    setMyBusy(true);
    setMyError('');
    try {
      await client.delete(`${url}/api/runningtrack/musicplaylistitem/${item.clubMusicPlaylistItemID}`);
      await loadEditingItems(editingListID);
      await loadMyLists();
    } catch (e) {
      setMyError('곡을 삭제하지 못했습니다.');
    }
    setMyBusy(false);
  };

  //--> 순서 변경은 ▲▼ 로 한 칸씩. 화면을 먼저 바꾸고 서버에 순서를 통째로 저장한다.
  const handleMoveTrack = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= editingItems.length) return;
    const reordered = editingItems.slice();
    const tmp = reordered[index];
    reordered[index] = reordered[next];
    reordered[next] = tmp;
    setEditingItems(reordered);
    try {
      await client.post(`${url}/api/runningtrack/musicplaylist/${editingListID}/item/order`, {
        itemIDs: reordered.map(function(it) { return it.clubMusicPlaylistItemID; }),
      });
    } catch (e) {
      setMyError('순서를 저장하지 못했습니다.');
      await loadEditingItems(editingListID);
    }
  };

  const handlePlayMyList = async (list) => {
    setMyError('');
    let items = [];
    try {
      items = await fetchItems(list.clubMusicPlaylistID);
    } catch (e) {
      setMyError('재생목록을 불러오지 못했습니다.');
      return;
    }
    if (items.length === 0) {
      setMyError('빈 목록입니다. 곡을 먼저 추가해주세요.');
      return;
    }
    resetPlaybackState();
    setCurrentPlaylistIndex(-1);
    setPlayTarget({
      kind: 'custom',
      playlistID: list.clubMusicPlaylistID,
      title: list.title,
      tracks: items.map(function(it) {
        return {
          videoId: it.videoId,
          title: it.title || it.videoId,
          thumbnailUrl: it.thumbnailUrl || '',
        };
      }),
    });
    addEventLog(`내 재생목록 선택: ${list.title} (${items.length}곡)`);
  };

  // 트랙 클릭 시 해당 곡으로 이동
  const handleTrackClick = (index) => {
    if (playerRef.current && playerRef.current.playVideoAt) {
      playerRef.current.playVideoAt(index);
      setCurrentTrackIndex(index);
    }
  };

  // 현재 트랙 변경 시 자동 스크롤
  React.useEffect(() => {
    if (trackListRef.current && currentTrackIndex >= 0) {
      var items = trackListRef.current.children;
      if (items && items[currentTrackIndex]) {
        items[currentTrackIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [currentTrackIndex]);

  return (
      <PageWrapper>
        <ContentWrapper>
          {/* 재생 소스 탭 */}
          <TopBar>
            <TabRow>
              <TabButton
                active={sourceTab === 'shared'}
                onClick={function() { setSourceTab('shared'); }}
              >공용 목록</TabButton>
              <TabButton
                active={sourceTab === 'custom'}
                onClick={function() { setSourceTab('custom'); }}
              >내 재생목록</TabButton>
            </TabRow>
          </TopBar>

          {/* ── 아래 두 안내 배너는 요청에 따라 화면에서 뺐다. 감지 로직(bgBlockCount ·
                 autoplayPolicy · launchCommand)은 그대로 살아 있으므로, 백그라운드 재생이
                 다시 끊기면 이 주석만 풀어서 진단하면 된다. ──────────────────────────

          {bgBlockCount > 0 && !noticeDismissed && (
            <NoticeBox warn>
              <NoticeTitle warn>
                <span>⚠ 창이 가려진 동안 브라우저가 음악 재생을 중단시켰습니다 ({bgBlockCount}회)</span>
                <NoticeButton onClick={function() { setNoticeDismissed(true); }}>닫기</NoticeButton>
              </NoticeTitle>
              <NoticeText>
                이 창을 최소화하거나 다른 창으로 완전히 덮으면 브라우저가 재생을 멈추고 다음 곡을 시작하지 않습니다.
                아래 중 한 가지로 해결해 주세요.
              </NoticeText>
              <NoticeList>
                <li>이 창을 최소화하지 말고 일부라도 화면에 보이게 두기 (보조 모니터 권장)</li>
                <li>아래 명령으로 전용 크롬 창을 띄워 이 페이지 열기 (백그라운드 중단 정책 해제)</li>
              </NoticeList>
              <CommandBox>{launchCommand}</CommandBox>
              <NoticeButton onClick={handleCopyCommand}>{copied ? '복사됨' : '명령 복사'}</NoticeButton>
            </NoticeBox>
          )}

          {autoplayPolicy === 'allowed' && bgBlockCount === 0 && (
            <NoticeBox>
              <NoticeTitle>✓ 권장 실행 옵션이 적용된 창입니다. 창을 가려도 재생이 유지됩니다.</NoticeTitle>
            </NoticeBox>
          )}

              ────────────────────────────────────────────────────────────────── */}

          {/* 플레이어 + 트랙리스트 영역 */}
          <PlayerArea>
            <VideoSection>
              <VideoFrameWrapper compact={!playTarget}>
                {playTarget ? (
                  <VideoFrameInner>
                    <div id="youtube-player" style={{ width: '100%', height: '100%' }} />
                  </VideoFrameInner>
                ) : (
                  <EmptyPlayer>
                    <EmptyIcon>🎵</EmptyIcon>
                    <EmptyText>아래 목록에서 플레이리스트를 선택해주세요</EmptyText>
                  </EmptyPlayer>
                )}
              </VideoFrameWrapper>
            </VideoSection>

            {/* 트랙 리스트 (플레이리스트 선택 후 표시) */}
            {playlistItems.length > 0 && (
              <TrackListPanel>
                <TrackListHeader>
                  <span>{currentVideo ? currentVideo.title : '트랙 목록'}</span>
                  <TrackListCount>{playlistItems.length}곡</TrackListCount>
                </TrackListHeader>
                <TrackListScroll ref={trackListRef}>
                  {playlistItems.map(function(track, index) {
                    var isActive = index === currentTrackIndex;
                    return (
                      <TrackItem
                        key={track.videoId + '-' + index}
                        active={isActive}
                        onClick={function() { handleTrackClick(index); }}
                      >
                        <TrackNumber active={isActive}>{isActive ? (
                          <PlayingBars>
                            <PlayingBar duration="0.8s" delay="0s" />
                            <PlayingBar duration="0.6s" delay="0.15s" />
                            <PlayingBar duration="0.9s" delay="0.3s" />
                          </PlayingBars>
                        ) : (index + 1)}</TrackNumber>
                        {track.thumbnailUrl && (
                          <TrackThumb src={track.thumbnailUrl} alt="" />
                        )}
                        <TrackItemTitle active={isActive}>{track.title}</TrackItemTitle>
                      </TrackItem>
                    );
                  })}
                </TrackListScroll>
              </TrackListPanel>
            )}
          </PlayerArea>

          {/* 공용 목록 — 어드민이 등록한 유튜브 플레이리스트 */}
          {sourceTab === 'shared' && (
          <PlaylistArea>
            <PlaylistHeader>
              <PlaylistTitle>재생 목록</PlaylistTitle>
              <PlaylistCount>{playlist.length}개의 플레이리스트</PlaylistCount>
            </PlaylistHeader>
            <PlaylistGrid>
              {playlist.map((video, index) => {
                const isActive = index === currentPlaylistIndex;
                return (
                  <PlaylistCard
                    key={`${video.listId}-${index}`}
                    active={isActive}
                    onClick={() => handleSelectPlaylist(index)}
                  >
                    <CardThumbWrapper>
                      {video.thumbnailUrl ? (
                        <CardThumbImg src={video.thumbnailUrl} alt="" />
                      ) : (
                        <CardNoThumb>🎵</CardNoThumb>
                      )}
                      {isActive && (
                        <CardPlayingOverlay>
                          <PlayingBars>
                            <PlayingBar duration="0.8s" delay="0s" />
                            <PlayingBar duration="0.6s" delay="0.15s" />
                            <PlayingBar duration="0.9s" delay="0.3s" />
                          </PlayingBars>
                        </CardPlayingOverlay>
                      )}
                    </CardThumbWrapper>
                    <CardInfo>
                      <CardTitle active={isActive}>{video.title}</CardTitle>
                      <CardSub>YouTube Music · 재생목록</CardSub>
                    </CardInfo>
                  </PlaylistCard>
                );
              })}
            </PlaylistGrid>
          </PlaylistArea>
          )}

          {/* 내 재생목록 — 지점이 직접 만드는 목록 */}
          {sourceTab === 'custom' && (
          <PlaylistArea>
            <PlaylistHeader>
              <PlaylistTitle>내 재생목록</PlaylistTitle>
              <PlaylistCount>{myLists.length}개의 목록</PlaylistCount>
            </PlaylistHeader>

            {!clubId ? (
              <EmptyNote>
                클럽 로그인이 필요합니다. 뮤직 플레이어 앱으로 접속하거나, 관리자 화면에서 다시 열어주세요.
              </EmptyNote>
            ) : (
              <>
                <MyListBar>
                  <TextInput
                    placeholder="새 재생목록 이름"
                    value={newListTitle}
                    onChange={function(e) { setNewListTitle(e.target.value); }}
                    onKeyDown={function(e) { if (e.key === 'Enter') handleCreateList(); }}
                  />
                  <ActionButton primary disabled={myBusy} onClick={handleCreateList}>+ 만들기</ActionButton>
                </MyListBar>

                {myError && <MyErrorText>{myError}</MyErrorText>}

                {myListsLoaded && myLists.length === 0 ? (
                  <EmptyNote>아직 만든 재생목록이 없습니다. 위에서 이름을 지어 만들어보세요.</EmptyNote>
                ) : (
                  <PlaylistGrid>
                    {myLists.map(function(list) {
                      var isActive = playTarget && playTarget.kind === 'custom'
                        && playTarget.playlistID === list.clubMusicPlaylistID;
                      return (
                        <PlaylistCard key={list.clubMusicPlaylistID} active={isActive}>
                          <CardThumbWrapper onClick={function() { handlePlayMyList(list); }} style={{ cursor: 'pointer' }}>
                            <CardNoThumb>🎧</CardNoThumb>
                            {isActive && (
                              <CardPlayingOverlay>
                                <PlayingBars>
                                  <PlayingBar duration="0.8s" delay="0s" />
                                  <PlayingBar duration="0.6s" delay="0.15s" />
                                  <PlayingBar duration="0.9s" delay="0.3s" />
                                </PlayingBars>
                              </CardPlayingOverlay>
                            )}
                          </CardThumbWrapper>
                          <CardInfo>
                            <CardTitle active={isActive}>{list.title}</CardTitle>
                            <CardSub>{list.itemCount || 0}곡</CardSub>
                            <CardActions>
                              <CardActionButton primary onClick={function() { handlePlayMyList(list); }}>재생</CardActionButton>
                              <CardActionButton onClick={function() { handleOpenEditor(list); }}>
                                {editingListID === list.clubMusicPlaylistID ? '닫기' : '편집'}
                              </CardActionButton>
                              <CardActionButton danger onClick={function() { handleDeleteList(list); }}>삭제</CardActionButton>
                            </CardActions>
                          </CardInfo>
                        </PlaylistCard>
                      );
                    })}
                  </PlaylistGrid>
                )}

                {editingListID && (
                  <EditorPanel>
                    <EditorTitle>
                      <span>목록 편집</span>
                      <ActionButton onClick={function() { setEditingListID(null); }}>닫기</ActionButton>
                    </EditorTitle>

                    <EditorRow>
                      <TextInput
                        value={editingTitle}
                        onChange={function(e) { setEditingTitle(e.target.value); }}
                        onKeyDown={function(e) { if (e.key === 'Enter') handleRenameList(); }}
                      />
                      <ActionButton disabled={myBusy} onClick={handleRenameList}>이름 저장</ActionButton>
                    </EditorRow>

                    <EditorRow>
                      <TextInput
                        wide
                        placeholder="유튜브 영상 주소 붙여넣기 (https://www.youtube.com/watch?v=...)"
                        value={newVideoUrl}
                        onChange={function(e) { setNewVideoUrl(e.target.value); }}
                        onKeyDown={function(e) { if (e.key === 'Enter') handleAddTrack(); }}
                      />
                      <ActionButton primary disabled={myBusy} onClick={handleAddTrack}>+ 곡 추가</ActionButton>
                    </EditorRow>

                    <EditorHint>
                      영상 주소 · youtu.be 짧은 주소 · Shorts 주소 · 영상 ID 를 모두 인식합니다.
                      {playTarget && playTarget.kind === 'custom' && playTarget.playlistID === editingListID
                        && ' 지금 재생 중인 목록입니다 — 바꾼 내용은 이 목록을 다시 선택하면 적용됩니다.'}
                    </EditorHint>

                    {editingItems.length === 0 ? (
                      <EmptyNote>담긴 곡이 없습니다.</EmptyNote>
                    ) : (
                      editingItems.map(function(item, index) {
                        return (
                          <EditItemRow key={item.clubMusicPlaylistItemID}>
                            <EditItemIndex>{index + 1}</EditItemIndex>
                            {item.thumbnailUrl
                              ? <EditItemThumb src={item.thumbnailUrl} alt="" />
                              : <EditItemIndex>🎵</EditItemIndex>}
                            <EditItemTitle>{item.title}</EditItemTitle>
                            <IconButton
                              disabled={index === 0}
                              onClick={function() { handleMoveTrack(index, -1); }}
                            >▲</IconButton>
                            <IconButton
                              disabled={index === editingItems.length - 1}
                              onClick={function() { handleMoveTrack(index, 1); }}
                            >▼</IconButton>
                            <IconButton danger onClick={function() { handleDeleteTrack(item); }}>✕</IconButton>
                          </EditItemRow>
                        );
                      })
                    )}
                  </EditorPanel>
                )}
              </>
            )}
          </PlaylistArea>
          )}

          {/* ── 이벤트 로그는 요청에 따라 화면에서 뺐다. addEventLog 는 계속 기록하고 있으므로
                 (eventLogs 최근 100건) 문제가 생기면 이 주석만 풀면 그대로 보인다. ──────

          <EventLogSection>
            <EventLogTitle>이벤트 로그</EventLogTitle>
            <EventLogList>
              {eventLogs.map(function(log, index) {
                return (
                  <EventLogItem key={index}>
                    [{log.time}] {log.message}
                  </EventLogItem>
                );
              })}
            </EventLogList>
          </EventLogSection>

              ────────────────────────────────────────────────────────────────── */}
        </ContentWrapper>
      </PageWrapper>
  );
};

export default RunningTrackMusicPage;
